import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/query-keys'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  discardInstrumentLiveEdit,
  republishInstrument,
  startInstrumentLiveEdit,
} from '@/features/instruments/infra/instrumentLiveEdit.api'
import { publishDraftQueryKeys } from '@/features/instruments/publish/shared/api/publish-draft-queries'

function invalidateLiveEditQueries(
  queryClient: QueryClient,
  tenantId: string | null | undefined,
  instrumentId: string,
) {
  const tenant = tenantId ?? '_'
  void queryClient.invalidateQueries({ queryKey: queryKeys.instruments.draft(instrumentId) })
  // Drop cached publish-wizard draft so Survey Edit cannot hydrate empty
  // `channelContent` from a pre-edit GET (`staleTime` + refetchOnMount: false).
  queryClient.removeQueries({ queryKey: publishDraftQueryKeys.detail(instrumentId) })
  void queryClient.invalidateQueries({ queryKey: queryKeys.instruments.all(tenant) })
  void queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all(tenant) })
}

export function useStartLiveEdit() {
  const queryClient = useQueryClient()
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  return useMutation({
    mutationFn: (instrumentId: string) => startInstrumentLiveEdit(instrumentId),
    onSuccess: (_state, instrumentId) => {
      invalidateLiveEditQueries(queryClient, tenantId, instrumentId)
    },
  })
}

export function useDiscardLiveEdit() {
  const queryClient = useQueryClient()
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  return useMutation({
    mutationFn: (instrumentId: string) => discardInstrumentLiveEdit(instrumentId),
    onSuccess: (_data, instrumentId) => {
      invalidateLiveEditQueries(queryClient, tenantId, instrumentId)
    },
  })
}

export function useRepublishInstrument() {
  const queryClient = useQueryClient()
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  return useMutation({
    mutationFn: (params: { instrumentId: string; idempotencyKey?: string }) =>
      republishInstrument(params),
    onSuccess: (_result, variables) => {
      invalidateLiveEditQueries(queryClient, tenantId, variables.instrumentId)
      void queryClient.invalidateQueries({
        queryKey: queryKeys.instrumentDashboard.all(tenantId ?? '_'),
      })
    },
  })
}
